/*
 * React adaptation of Motion Core's Specular Band component.
 * Copyright (c) 2025 Marek Jóźwiak. Licensed under the MIT License.
 * https://github.com/motion-core/motion-core/tree/master/packages/motion-core/src/lib/components/specular-band
 */
import { Mesh, Program, Renderer, Transform, Triangle, Vec2, Vec3 } from "ogl";
import { useEffect, useRef } from "react";

const settings = {
  color: [0xff, 0xeb, 0xc2] as const,
  backgroundColor: [0xff, 0xfe, 0xfa] as const,
  speed: 0.13,
  distortion: 0.3,
  hueShift: 22,
  intensity: 2.94,
};

const vertexShader = `
  attribute vec2 uv;
  attribute vec2 position;
  varying vec2 vUv;

  void main() {
    vUv = uv;
    gl_Position = vec4(position, 0.0, 1.0);
  }
`;

const fragmentShader = `
  precision highp float;
  varying vec2 vUv;

  uniform float uTime;
  uniform vec2 uResolution;
  uniform vec3 uColor;
  uniform vec3 uBackgroundColor;
  uniform float uSpeed;
  uniform float uDistortion;
  uniform float uHueShift;
  uniform float uIntensity;

  mat3 hueRot(float a) {
    float c = cos(a), s = sin(a), t = 1.0 - c;
    return mat3(
      t*.333+c, t*.333-s*.577, t*.333+s*.577,
      t*.333+s*.577, t*.333+c, t*.333-s*.577,
      t*.333-s*.577, t*.333+s*.577, t*.333+c
    );
  }

  float colorLuma(vec3 c) {
    return dot(c, vec3(0.2126, 0.7152, 0.0722));
  }

  vec3 hueFromColor(vec3 c, vec3 fallback) {
    float m = max(max(c.r, c.g), c.b);
    if (m < 1e-5) return fallback;
    return clamp(c / m, 0.0, 1.0);
  }

  vec3 blendAdaptive(vec3 bg, vec3 effect, float softness) {
    float bgLum = colorLuma(bg);
    float lightBg = smoothstep(0.45, 0.95, bgLum);
    float edge = clamp(softness, 0.0, 1.0);
    vec3 additive = bg + effect;
    vec3 effectHue = hueFromColor(effect, vec3(1.0));
    vec3 tintTarget = mix(bg, effectHue, 0.9);
    vec3 tint = mix(bg, tintTarget, edge);
    return mix(additive, tint, lightBg);
  }

  vec3 linearToSrgb(vec3 color) {
    vec3 safe = max(color, vec3(0.0));
    vec3 low = safe * 12.92;
    vec3 high = 1.055 * pow(safe, vec3(1.0 / 2.4)) - 0.055;
    vec3 cutoff = step(vec3(0.0031308), safe);
    return mix(low, high, cutoff);
  }

  void mainImage(out vec4 o, vec2 uv) {
    vec2 u = uv * 2.0 - 1.0;
    u.x *= uResolution.x / uResolution.y;
    float time = uTime * uSpeed;
    u /= 0.5 + uDistortion * dot(u, u);
    u += 0.2 * cos(time) - 7.56;

    vec3 palette[3];
    palette[0] = uColor;
    palette[1] = hueRot(radians(uHueShift)) * uColor;
    palette[2] = hueRot(radians(-uHueShift)) * uColor;

    vec3 col = vec3(0.0);
    float edgeField = 0.0;
    for (int i = 0; i < 3; i++) {
      vec2 uvLoop = sin(1.5 * u.yx + 2.0 * cos(u -= 0.01));
      float val = 1.0 - exp(-6.0 / exp(6.0 * length(uvLoop + sin(5.0 * uvLoop.y - 3.0 * time) / 4.0)));
      val = pow(clamp(val, 0.0, 1.0), 1.4);
      edgeField += val;
      col += val * palette[i];
    }

    vec3 bands = col * uIntensity;
    float softMask = 1.0 - exp(-0.85 * edgeField * uIntensity);
    o = vec4(blendAdaptive(uBackgroundColor, bands, softMask), 1.0);
  }

  void main() {
    vec4 fragColor;
    mainImage(fragColor, vUv);
    fragColor.rgb = linearToSrgb(fragColor.rgb);
    gl_FragColor = fragColor;
  }
`;

function linearRgb([red, green, blue]: readonly [number, number, number]) {
  const linearChannel = (channel: number) => {
    const value = channel / 255;
    return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  };
  return new Vec3(linearChannel(red), linearChannel(green), linearChannel(blue));
}

export function SpecularBandBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    let renderer: Renderer;
    try {
      renderer = new Renderer({
        canvas,
        alpha: false,
        depth: false,
        autoClear: false,
        dpr: Math.min(window.devicePixelRatio || 1, 2),
      });
    } catch (error) {
      console.warn("[basket] WebGL background is unavailable", error);
      return;
    }
    const fillViewport = () => {
      // OGL writes pixel dimensions inline; restore the responsive canvas size.
      canvas.style.width = "100%";
      canvas.style.height = "100%";
    };
    fillViewport();

    const gl = renderer.gl;
    const scene = new Transform();
    const geometry = new Triangle(gl);
    const uniforms = {
      uTime: { value: 0 },
      uResolution: { value: new Vec2(1, 1) },
      uColor: { value: linearRgb(settings.color) },
      uBackgroundColor: { value: linearRgb(settings.backgroundColor) },
      uSpeed: { value: settings.speed },
      uDistortion: { value: settings.distortion },
      uHueShift: { value: settings.hueShift },
      uIntensity: { value: settings.intensity },
    };
    const program = new Program(gl, {
      vertex: vertexShader,
      fragment: fragmentShader,
      uniforms,
      depthTest: false,
      depthWrite: false,
    });
    const mesh = new Mesh(gl, { geometry, program, frustumCulled: false });
    mesh.setParent(scene);

    const motionPreference = window.matchMedia("(prefers-reduced-motion: reduce)");
    let reducedMotion = motionPreference.matches;
    let animationFrame = 0;
    let previous = 0;
    let disposed = false;
    let canvasWidth = 0;
    let canvasHeight = 0;

    const resize = () => {
      const width = Math.max(1, canvas.clientWidth);
      const height = Math.max(1, canvas.clientHeight);
      if (width === canvasWidth && height === canvasHeight) return false;
      canvasWidth = width;
      canvasHeight = height;
      renderer.setSize(width, height);
      fillViewport();
      uniforms.uResolution.value.set(width, height);
      return true;
    };

    const render = (now: number) => {
      if (disposed) return;
      const delta = previous ? (now - previous) / 1000 : 0;
      previous = now;
      uniforms.uTime.value += delta;
      renderer.render({ scene, update: false, sort: false, frustumCull: false, clear: false });
      if (!reducedMotion && !document.hidden) animationFrame = window.requestAnimationFrame(render);
    };

    const start = () => {
      window.cancelAnimationFrame(animationFrame);
      previous = 0;
      animationFrame = window.requestAnimationFrame(render);
    };
    const handleVisibilityChange = () => {
      if (document.hidden) window.cancelAnimationFrame(animationFrame);
      else start();
    };
    const handleMotionPreference = (event: MediaQueryListEvent) => {
      reducedMotion = event.matches;
      start();
    };
    const resizeObserver = new ResizeObserver(() => {
      if (resize() && reducedMotion) start();
    });

    resize();
    resizeObserver.observe(canvas);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    motionPreference.addEventListener("change", handleMotionPreference);
    start();

    return () => {
      disposed = true;
      window.cancelAnimationFrame(animationFrame);
      resizeObserver.disconnect();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      motionPreference.removeEventListener("change", handleMotionPreference);
      mesh.setParent(null);
      program.remove();
      geometry.remove();
    };
  }, []);

  return <canvas ref={canvasRef} className="basket-specular-background" aria-hidden="true" />;
}
